import React, { useEffect, useState, memo } from 'react';

// Module-level memoization of already-loaded image URLs. This persists across
// component unmounts so that navigating away and back to a page (e.g. landing
// -> register -> back) does NOT re-trigger the fade-in for an image that is
// already in the browser cache. The Set survives until full page reload.
const loadedSrcs = new Set();

/**
 * <SmartImage> — drop-in replacement for <img> that eliminates the flicker
 * caused by re-renders / navigation.
 *
 * Behaviour:
 *   1. Renders a soft beige placeholder (animated pulse) until `onLoad` fires.
 *   2. Image fades from opacity 0 → 1 over 300ms once decoded.
 *   3. Uses `loading="lazy"` and `decoding="async"` by default.
 *   4. Remembers loaded `src`s in a module-level Set so subsequent renders
 *      skip the fade and show the image instantly.
 *   5. Wrapped in React.memo to avoid re-renders when props are unchanged.
 *
 * API:
 *   <SmartImage
 *     src="..."
 *     alt="..."
 *     className="..."          // applied to the WRAPPER (sizing, rounded, ring, shadow)
 *     imgClassName="..."       // extra classes for the inner <img>
 *     style={...}              // forwarded to the inner <img> (objectPosition etc.)
 *     objectFit="cover"        // "cover" | "contain" | "fill" | "none"
 *     loading="lazy"           // override default
 *     onClick={...}
 *     ... // any other <img> attributes pass through via {...rest}
 *   />
 */
const SmartImage = memo(function SmartImage({
  src,
  alt = '',
  className = '',
  imgClassName = '',
  style,
  objectFit = 'cover',
  loading = 'lazy',
  decoding = 'async',
  onLoad,
  onClick,
  placeholderClassName = '',
  ...rest
}) {
  // Initialize as "loaded" if the URL has already been decoded once during
  // this session — prevents the fade-in on revisit / re-render.
  const [loaded, setLoaded] = useState(() => loadedSrcs.has(src));

  // If the `src` prop changes, re-evaluate whether the new URL is known.
  useEffect(() => {
    setLoaded(loadedSrcs.has(src));
  }, [src]);

  const handleLoad = (e) => {
    if (src) loadedSrcs.add(src);
    setLoaded(true);
    if (onLoad) onLoad(e);
  };

  const fitClass = {
    cover: 'object-cover',
    contain: 'object-contain',
    fill: 'object-fill',
    none: 'object-none',
  }[objectFit] || 'object-cover';

  // Only impose `relative` positioning when the caller hasn't set their own
  // (e.g. `absolute inset-0` for stacked/overlapping layouts). Without this
  // check, Tailwind's compiled CSS order would let `relative` override the
  // caller's `absolute`, breaking stacked use cases like rotation overlays.
  const hasOwnPosition = /\b(absolute|fixed|sticky|relative)\b/.test(className);
  const positionClass = hasOwnPosition ? '' : 'relative';

  return (
    <div className={`${positionClass} overflow-hidden ${className}`}>
      {/* Soft placeholder — visible until the image has loaded. The pulse
          animation is gentle and matches the warm palette so it never feels
          jarring. Once `loaded` flips true, it fades out in parallel with the
          image fading in, giving a clean crossfade effect. */}
      <div
        aria-hidden="true"
        className={`absolute inset-0 transition-opacity duration-300 ${loaded ? 'opacity-0' : 'opacity-100'} ${placeholderClassName || 'bg-terracotta-light/15 animate-pulse'}`}
      />
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        onLoad={handleLoad}
        onClick={onClick}
        style={style}
        className={`relative w-full h-full ${fitClass} transition-opacity duration-300 ${loaded ? 'opacity-100' : 'opacity-0'} ${imgClassName}`}
        {...rest}
      />
    </div>
  );
});

export default SmartImage;
